import { useEffect, useState, useRef, useMemo } from "react";
import "../styles/gis/gis.scss";
import MenuTab from "../components/menu_tab";
import GISHeader from "../components/gis/gisheader";
import myData from "../assets/data/data";
import { FaLayerGroup, FaTimes, FaMapMarkerAlt, FaMap, FaExpand, FaCompress } from "react-icons/fa";
import northarrow from "../assets/icons/north-arrow.png"

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import { XYZ } from "ol/source";
import { ZoomToExtent, defaults as defaultControls } from "ol/control";
import LoadingWidget from "../components/loading";
import { Style, Circle as CircleStyle, Fill, Stroke } from "ol/style";
import Overlay from "ol/Overlay";
import Legend from "../components/gis/legend";
import SearchBar from "../components/searchbar";
import { locateUser } from "../components/gis/locateUser";
import html2canvas from "html2canvas";
import TabularData from "../components/tabular_data";
import { getLayerSchema, buildClassification, SHOW_ALL_KEY, getShowAllClass } from "../components/gis/attributeAnalyzer";
import { buildPopupHTML } from "../components/gis/popupBuilder";
import Icon from 'ol/style/Icon';
import { getColoredMeterSVG } from "../assets/icons/legendicons/mlegendicons";
import MediaLightbox from "../components/gis/mediaLightbox";

export default function MeterUpdation() {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [map, setMap] = useState(null);
  const [basemapSelected, setBasemapSelected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [basemapsVisible, setBasemapsVisible] = useState(true);
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, label } | null

  const [layers, setLayers] = useState({
    "Meter Replacements": {
      url: "/api/meterreplacement",
      data: [],
      visibility: true,
      activeAttribute: null,
      classFilters: {},
    },
    "New Meter Installations": {
      url: "/api/newcustomers",
      data: [],
      visibility: true,
      activeAttribute: null,
      classFilters: {},
    },
  });

  const [selectedFeature, setSelectedFeature] = useState({ layerName: null, id: null });

  const mapElement = useRef();
  const mapWrapperRef = useRef();
  const popupElement = useRef();
  const popupOverlay = useRef();

  // Prefers an exact "status" or "account_status" field if present,
  // otherwise anything status-like, otherwise the first available field.
  const pickDefaultAttribute = (fields) => {
    const exactStatus = fields.find(
      (f) => f.toLowerCase() === "status" || f.toLowerCase() === "account_status"
    );
    if (exactStatus) return exactStatus;
    return fields.find((f) => /status/i.test(f)) || fields[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      const requests = Object.entries(layers).map(([key, layer]) =>
        fetch(layer.url).then((res) => (res.ok ? res.json() : Promise.reject(`Failed: ${key}`)))
      );

      const responses = await Promise.allSettled(requests);
      const updatedLayers = { ...layers };
      

      Object.keys(layers).forEach((key, i) => {
        if (responses[i].status === "fulfilled") {
          const rawData = responses[i].value;

          // Both endpoints ship raw lon/lat pairs instead of a geom
          // object. New connections have no "old" location at all, so
          // every record — replacement or new install — is placed at
          // its *current* meter position (new_x_lon/new_y_lat). This
          // keeps both layers plugging into the exact same rendering
          // pipeline as every geom-based layer elsewhere in the app.
          const data = rawData
            .filter((item) => item.new_x_lon != null && item.new_y_lat != null)
            .map((item) => ({
              ...item,
              geom: {
                type: "Point",
                coordinates: [Number(item.new_x_lon), Number(item.new_y_lat)],
              },
            }));

          const schema = getLayerSchema(data);
          const fields = schema.map((s) => s.field);

          updatedLayers[key] = {
            ...updatedLayers[key],
            data,
            activeAttribute: pickDefaultAttribute(fields) || null,
          };
          setIsLoading(false);
        } else {
          console.error(`Error fetching ${key}:`, responses[i].reason);
        }
      });

      setLayers(updatedLayers);
    };

    fetchData();
  }, []);

    useEffect(() => {
        const handlePhotoClick = (e) => {
            const target = e.target.closest(".popup-photo-item");
            if (!target) return;
            setLightbox({
            src: target.getAttribute("data-photo-src"),
            label: target.getAttribute("data-photo-label"),
            });
        };
        document.addEventListener("click", handlePhotoClick);
        return () => document.removeEventListener("click", handlePhotoClick);
    }, []);

  const layerSchemas = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      result[name] = getLayerSchema(cfg.data || []);
    });
    return result;
  }, [layers]);

  const layerClassifications = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      if (cfg.activeAttribute === SHOW_ALL_KEY) {
        result[name] = [getShowAllClass(name)];
        return;
      }
      const schema = layerSchemas[name] || [];
      const fieldInfo = schema.find((s) => s.field === cfg.activeAttribute);
      result[name] = fieldInfo ? buildClassification(cfg.data, cfg.activeAttribute, fieldInfo) : [];
    });
    return result;
  }, [layers, layerSchemas]);

  const handleAttributeChange = (layerName, field) => {
    setLayers((prev) => ({ ...prev, [layerName]: { ...prev[layerName], activeAttribute: field } }));
  };

  const handleToggleClass = (layerName, attribute, classKey) => {
    setLayers((prev) => {
      const cfg = prev[layerName];
      const currentFilters = cfg.classFilters?.[attribute] || {};
      const currentVal = currentFilters[classKey] !== undefined ? currentFilters[classKey] : true;
      return {
        ...prev,
        [layerName]: {
          ...cfg,
          classFilters: { ...cfg.classFilters, [attribute]: { ...currentFilters, [classKey]: !currentVal } },
        },
      };
    });
  };

  useEffect(() => {
    const basemapLayer = new TileLayer({
      title: "Basemaps",
      source: new XYZ({ url: myData[basemapSelected].url, crossOrigin: "Anonymous" }),
    });

    const initialMap = new Map({
      target: mapElement.current,
      layers: [basemapLayer],
      view: new View({
        projection: "EPSG:3857",
        center: fromLonLat([36.777382, -1.040343]),
        zoom: 12.5,
        maxZoom: 32,
      }),
      controls: defaultControls().extend([
        new ZoomToExtent({
          extent: [...fromLonLat([36.6970, -1.1335]), ...fromLonLat([36.8846, -0.9800])],
        }),
      ]),
    });

    setMap(initialMap);
    return () => initialMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    if (!map) return;
    const basemapLayer = map.getLayers().getArray().find((layer) => layer.get("title") === "Basemaps");
    if (basemapLayer) {
      basemapLayer.setSource(new XYZ({ url: myData[basemapSelected].url, crossOrigin: "Anonymous" }));
      basemapLayer.setVisible(basemapsVisible);
    }
  }, [map, basemapSelected, basemapsVisible]);

  useEffect(() => {
    if (!map) return;

    popupOverlay.current = new Overlay({
      element: popupElement.current,
      autoPan: true,
      positioning: "bottom-center",
      offset: [0, -15],
    });

    map.addOverlay(popupOverlay.current);

    map.on("singleclick", (event) => {
        const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);

        if (feature) {
        const properties = feature.getProperties();

        const categoryLabel = properties.category || properties.layerName;
        const accountLabel = properties.account ?? properties.connection ?? "";

        document.getElementById("popup-content").innerHTML = buildPopupHTML(properties, {
            hiddenFields: [
            "old_x_lon", "old_y_lat", "new_x_lon", "new_y_lat",
            "enumerator", "customer_name",
            ],
            title: categoryLabel,
            subtitle: accountLabel ? `Account: ${accountLabel}` : "",
        });

        popupOverlay.current.setPosition(event.coordinate);

        setTimeout(() => {
            const btn = document.getElementById("zoomToFeatureBtn");
            if (btn) {
            btn.onclick = () => {
                const geometry = feature.getGeometry();
                map.getView().fit(geometry.getExtent(), { maxZoom: 18, duration: 800 });
            };
            }
        }, 100);
        } else {
        popupOverlay.current.setPosition(undefined);
        }
    });
  }, [map]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => map?.updateSize(), 150);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [map]);

  const toggleFullscreen = () => {
    const el = mapWrapperRef.current;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    }
  };

    useEffect(() => {
    if (!map) return;

    // Both layers here are meter records, so both use the same meter icon —
    // tinted per-class by whichever attribute is currently active.
    const svgGenerators = {
        "Meter Replacements": getColoredMeterSVG,
        "New Meter Installations": getColoredMeterSVG,
    };

    Object.entries(layers).forEach(([layerName, layerConfig]) => {
        let vectorLayer = map.getLayers().getArray().find((l) => l.get("title") === layerName);

        if (!vectorLayer) {
        vectorLayer = new VectorLayer({ title: layerName, source: new VectorSource() });
        map.addLayer(vectorLayer);
        }

        const source = vectorLayer.getSource();
        source.clear();

        const activeAttribute = layerConfig.activeAttribute;
        const classes = layerClassifications[layerName] || [];
        const classFilters = layerConfig.classFilters?.[activeAttribute] || {};
        const isClassVisible = (key) => (classFilters[key] !== undefined ? classFilters[key] : true);

        if (layerConfig.visibility && layerConfig.data && activeAttribute) {
        const styleCache = {};
        const iconGenerator = svgGenerators[layerName];

        vectorLayer.setStyle((feature) => {
            const featureId = feature.get("featureId");
            const featureLayer = feature.get("layerName");
            const classKey = feature.get("classKey");

            const isSelected =
            selectedFeature && featureId === selectedFeature.id && featureLayer === selectedFeature.layerName;

            const baseStyle = styleCache[classKey];
            if (!isSelected) return baseStyle;

            return [
            baseStyle,
            new Style({
                image: new CircleStyle({ radius: 12, fill: new Fill({ color: "rgba(255,165,0,0.3)" }) }),
                zIndex: 999,
            }),
            ];
        });

        const features = layerConfig.data
            .filter((item) => item.geom)
            .map((item) => {
            const matchedClass = classes.find((c) => c.test(item[activeAttribute]));
            if (!matchedClass || !isClassVisible(matchedClass.key)) return null;

            const geometry = new Point(fromLonLat(item.geom.coordinates));
            const properties = {
                ...(activeAttribute !== SHOW_ALL_KEY ? { [activeAttribute]: item[activeAttribute] } : {}),
                ...item,
            };

            const feature = new Feature({ geometry, ...properties });
            const id = item.id || item.fid || item.gid;

            feature.set("featureId", id);
            feature.set("layerName", layerName);
            feature.set("classKey", matchedClass.key);

            if (!styleCache[matchedClass.key]) {
                styleCache[matchedClass.key] = iconGenerator
                ? new Style({
                    image: new Icon({
                        src: iconGenerator(matchedClass.color),
                        anchor: [0.5, 0.5],
                        scale: 0.6,
                        crossOrigin: "anonymous",
                    }),
                    })
                : new Style({
                    image: new CircleStyle({
                        radius: 7,
                        fill: new Fill({ color: matchedClass.color }),
                        stroke: new Stroke({ color: "#fff", width: 1.5 }),
                    }),
                    });
            }

            return feature;
            });

        source.addFeatures(features.filter(Boolean));
        }

        vectorLayer.setVisible(layerConfig.visibility);
    });
    }, [map, layers, selectedFeature, layerClassifications]);

  const exportMap = () => {
    if (!map) return;
    const title = prompt("Enter a title for the map:", "My Map Title");
    if (title === null) return;

    map.once("rendercomplete", () => {
      const mapCanvas = map.getViewport().querySelector("canvas");
      if (!mapCanvas) return;

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = mapCanvas.width;
      exportCanvas.height = mapCanvas.height;
      const ctx = exportCanvas.getContext("2d");
      ctx.drawImage(mapCanvas, 0, 0);

      const legend = document.querySelector(".legend");
      const northArrow = new Image();
      northArrow.src = northarrow;

      northArrow.onload = () => {
        ctx.drawImage(northArrow, exportCanvas.width - 250, 160, 245, 260);

        ctx.fillStyle = "black";
        ctx.fillRect(exportCanvas.width - 180, exportCanvas.height - 60, 100, 6);

        ctx.fillStyle = "white";
        ctx.font = "14px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("100m", exportCanvas.width - 170, exportCanvas.height - 65);

        const titleFontSize = 50;
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";

        const textWidth = ctx.measureText(title).width;
        const textX = exportCanvas.width / 2;
        const textY = 30;
        const padding = 40;

        ctx.fillStyle = "white";
        ctx.fillRect(textX - textWidth / 2 - padding / 2, textY - padding / 2, textWidth + padding, titleFontSize + padding);

        ctx.fillStyle = "black";
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.fillText(title, textX, textY);

        ctx.font = "italic 18px Arial";
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("© Githunguri Water and Sanitation Company Ltd.", 20, exportCanvas.height - 20);

        if (legend) {
          html2canvas(legend).then((legendCanvas) => {
            const legendX = exportCanvas.width - legendCanvas.width - 30;
            const legendY = exportCanvas.height - legendCanvas.height - 30;
            ctx.drawImage(legendCanvas, legendX, legendY);
            const link = document.createElement("a");
            link.download = "map_export.png";
            link.href = exportCanvas.toDataURL("image/png");
            link.click();
          });
        } else {
          const link = document.createElement("a");
          link.download = "map_export.png";
          link.href = exportCanvas.toDataURL("image/png");
          link.click();
        }
      };
    });

    map.renderSync();
  };

  const handleRowClick = (row, layerName) => {
    if (!map || !layers[layerName]) return;
    const id = row.id || row.fid || row.gid;
    setSelectedFeature({ layerName, id });

    const item = layers[layerName].data.find((d) => (d.id || d.fid || d.gid) === id);
    if (!item || !item.geom) return;

    const geometry = new Point(fromLonLat(item.geom.coordinates));
    map.getView().fit(geometry.getExtent(), { padding: [40, 200, 60, 60], duration: 600, maxZoom: 20 });
  };

  return (
    <div className="gis">
      <GISHeader toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />
      {toggleMenu && <MenuTab toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />}

      <div className="map" ref={mapWrapperRef}>
        {isLoading && <LoadingWidget />}

        <div className="map_container" ref={mapElement}>
          <div ref={popupElement} className="ol-popup">
            <button className="ol-popup-closer" onClick={() => popupOverlay.current.setPosition(undefined)}>
              <FaTimes />
            </button>
            <div id="popup-content"></div>
          </div>
        </div>

        <div className="map-buttons">
          {legendCollapsed && (
            <button onClick={() => setLegendCollapsed(false)}>
              <FaLayerGroup />
              <span className="tooltip">Layers</span>
            </button>
          )}
          <button onClick={() => locateUser(map)}>
            <FaMapMarkerAlt />
            <span className="tooltip">Locate Me</span>
          </button>
          <button onClick={() => { setLegendCollapsed(false); exportMap(); }}>
            <FaMap />
            <span className="tooltip">Export Map</span>
          </button>
          <button onClick={toggleFullscreen}>
            {isFullscreen ? <FaCompress /> : <FaExpand />}
            <span className="tooltip">{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
          </button>
        </div>

        <TabularData layers={layers} setLayers={setLayers} handleRowClick={handleRowClick} />
        <SearchBar layers={layers} setLayers={setLayers} />

        {!legendCollapsed && (
          <Legend
            layers={layers}
            setLayers={setLayers}
            layerSchemas={layerSchemas}
            layerClassifications={layerClassifications}
            onAttributeChange={handleAttributeChange}
            onToggleClass={handleToggleClass}
            myData={myData}
            legendCollapsed={legendCollapsed}
            setLegendCollapsed={setLegendCollapsed}
            basemapsVisible={basemapsVisible}
            setBasemapsVisible={setBasemapsVisible}
            basemapSelected={basemapSelected}
            setBasemapSelected={setBasemapSelected}
          />
        )}
        <MediaLightbox data={lightbox} onClose={() => setLightbox(null)} />
      </div>
    </div>
  );
}